'use client';

import { FC, lazy, memo, ReactNode } from 'react';

import MediaInfoGrid from '@/components/CaseStudy/MediaInfoGrid';
import LazySection from '@/components/LazySection';
import { BreadcrumbItem } from '@/lib/breadcrumbs/types';
import { CaseStudyDto } from '@/types/case-studies';

import css from './style.module.scss';

const Hero = lazy(() => import('@/components/CaseStudy/Hero'));
const StatsBlock = lazy(() => import('@/components/CaseStudy/StatsBlock'));
const TextHighlightBlockWithQuote = lazy(
	() => import('@/components/CaseStudy/TextBlock/TextHighlightBlockWithQuote')
);
const MediaOnlyBlock = lazy(() => import('@/components/CaseStudy/MediaOnlyBlock'));
const HeroCaseStudy = lazy(() => import('@/components/CaseStudyV2/Hero'));
const ResultsBlock = lazy(() => import('@/components/CaseStudyV2/ResultsBlock'));
const MediaCopy = lazy(() => import('@/components/CaseStudyV2/MediaCopy'));
const MediaCopyGrid = lazy(() => import('@/components/CaseStudyV2/MediaCopyGrid'));
const MediaCaseBlock = lazy(() => import('@/components/CaseStudyV2/MediaCaseBlock'));
const CopyQuoteBlock = lazy(() => import('@/components/CaseStudyV2/CopyQuoteBlock'));
const Recognition = lazy(() => import('@/components/CaseStudyV2/Recognition'));
const KudosTeam = lazy(() => import('@/components/CaseStudyV2/KudosTeam'));
const HowWeWorkSlider = lazy(() => import('@/components/CaseStudyV2/HowWeWorkSlider'));
const GuidelineForm = lazy(() => import('@/components/BrandingPage/GuidelineForm'));

const components: Partial<Record<string, FC<any>>> = {
	'case.hero': memo(Hero),
	'case.media-block': memo(MediaOnlyBlock),
	'case.text-highlight-block-with-quote': memo(TextHighlightBlockWithQuote),
	'case.stats': memo(StatsBlock),
	'case.media-info-grid': memo(MediaInfoGrid),

	'case-study.hero-case': memo(HeroCaseStudy),
	'case-study.results-block': memo(ResultsBlock),
	'case-study.media-copy': memo(MediaCopy),
	'case-study.media-copy-grid': memo(MediaCopyGrid),
	'case-study.media-case': memo(MediaCaseBlock),
	'case-study.quote-block': memo(CopyQuoteBlock),
	'case-study.kudos-team': memo(KudosTeam),
	'branding-page.guideline-form': memo(GuidelineForm),
	'case-study.how-we-work-slider': memo(HowWeWorkSlider)
};

interface Props {
	data: CaseStudyDto;
	breadcrumbItems?: BreadcrumbItem[];
	relevantCases?: ReactNode;
}

const CaseInner: FC<Props> = ({ data, breadcrumbItems, relevantCases }) => (
	<div className={css.wrapper}>
		<div className={css.content}>
			{data?.content?.length
				? data?.content?.map((block: any, index: number) => {
						const Component = components[block.__component as keyof typeof components];

						return Component ? (
							<Component
								key={`block_${index}`}
								data={block}
								caseAwards={data.awards}
								caseTags={data.tags}
								withDivider={block.withDivider}
								{...((block.__component === 'case.hero' ||
									block.__component === 'case-study.hero-case') &&
								breadcrumbItems
									? { breadcrumbItems }
									: {})}
							/>
						) : null;
					})
				: null}
		</div>

		<LazySection>
			<Recognition caseAwards={data.awards} />
		</LazySection>

		<LazySection>{relevantCases}</LazySection>
	</div>
);

export default CaseInner;
